import { useState, useEffect, useCallback, useRef } from 'react';
import { ContentService } from '@/services/contentService';
import type { SearchResult } from '@/services/contentService';
import { Rubrique, Serie, Episode } from '@/types';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { persistentContentCache } from '@/services/persistentContentCacheService';
import { useCacheInvalidationListener } from '@/hooks/useCacheInvalidationListener';
import { useRetryableFetch } from '@/hooks/useRetryableFetch';
import { campaignCache } from '@/services/campaignCacheService';
import { withSupabaseResult } from '@/utils/supabase/withSupabaseResult';
import { createScopedLogger } from '@/utils/logging/createScopedLogger';

const contentLog = createScopedLogger('USE-CONTENT');

async function enrichWithSeasonCounts(series: Serie[]): Promise<Serie[]> {
  if (series.length === 0) return series;
  const seriesIds = series.map(s => parseInt(s.id));
  const { data } = await withSupabaseResult<{ series_id: number; season_position: number }[]>(
    supabase
      .from('unified_contents_series_episodes')
      .select('series_id, season_position')
      .in('series_id', seriesIds),
    { operation: 'useContent.enrichWithSeasonCounts', logger: contentLog, fallback: [] }
  );

  if (!data || data.length === 0) return series;

  const seasonsBySeriesId = new Map<number, Set<number>>();
  for (const ep of data) {
    if (!seasonsBySeriesId.has(ep.series_id)) {
      seasonsBySeriesId.set(ep.series_id, new Set());
    }
    seasonsBySeriesId.get(ep.series_id)!.add(ep.season_position);
  }

  return series.map(s => {
    const seasons = seasonsBySeriesId.get(parseInt(s.id));
    return seasons ? { ...s, totalSeasons: seasons.size } : s;
  });
}

export const useSearch = (campaignCountriesLanguagesId: string | null, query: string) => {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const searchContent = async () => {
      if (!campaignCountriesLanguagesId || !query.trim()) {
        setResults([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const searchResults = await ContentService.search(campaignCountriesLanguagesId, query);
        setResults(searchResults);
      } catch (err) {
        setError('Error searching content');
        console.error(err);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    // Debounce search to avoid too many API calls
    const timeoutId = setTimeout(searchContent, 300);
    return () => clearTimeout(timeoutId);
  }, [campaignCountriesLanguagesId, query]);

  return { results, loading, error };
};

export const useTopViewedSeries = (
  campaignCountriesLanguagesId: string | null,
  limit: number = 3,
  fallbackCampaignCountriesLanguagesId: string | null = null
) => {
  const [series, setSeries] = useState<Serie[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const cacheVersion = useCacheInvalidationListener();
  const isInitialMountRef = useRef(true);
  const isFetchingRef = useRef(false);
  const prevCampaignIdRef = useRef<string | null>(null);

  const fetchTopSeries = useCallback(async (isInitialLoad: boolean) => {
    if (!campaignCountriesLanguagesId) {
      setSeries([]);
      // Don't set loading=false if we might get a campaign later
      return;
    }

    if (isFetchingRef.current) {
      console.log('⏭️ [useTopViewedSeries] Fetch already in progress, skipping...');
      return;
    }

    isFetchingRef.current = true;

    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        console.log(`♻️ [useTopViewedSeries] Cache invalidated (v${cacheVersion}), starting silent refetch...`);
      }

      const cachedSeries = await persistentContentCache.getTopViewedSeries(campaignCountriesLanguagesId);
      if (cachedSeries && cachedSeries.length > 0) {
        console.log(`useTopViewedSeries: ✅ Using cached top viewed series (${cachedSeries.length} items)`);
        const enriched = await enrichWithSeasonCounts(cachedSeries);
        setSeries(enriched);
        setError(null);
        if (isInitialLoad) setLoading(false);
        isFetchingRef.current = false;
        return;
      }

      if (cachedSeries && cachedSeries.length === 0) {
        console.log('useTopViewedSeries: ⚠️ Cache returned empty array, fetching from Supabase...');
      } else {
        console.log('useTopViewedSeries: 📡 No cache found, fetching from Supabase...');
      }

      const data = await ContentService.getTopViewedSeries(campaignCountriesLanguagesId, limit, fallbackCampaignCountriesLanguagesId);

      if (data && data.length > 0) {
        await persistentContentCache.setTopViewedSeries(campaignCountriesLanguagesId, data);
        console.log(`useTopViewedSeries: ✅ Fetched and cached ${data.length} top viewed series`);
      } else {
        console.log('useTopViewedSeries: ⚠️ Supabase returned empty data, not caching');
      }

      setSeries(data);
      setError(null);

      if (!isInitialLoad) {
        console.log('✅ [useTopViewedSeries] Silent refetch completed successfully');
      }
    } catch (err) {
      const errorMsg = 'Erreur lors du chargement des séries populaires';
      setError(errorMsg);
      console.error(isInitialLoad ? errorMsg : '❌ [useTopViewedSeries] Silent refetch failed:', err);
    } finally {
      if (isInitialLoad) setLoading(false);
      isFetchingRef.current = false;
    }
  }, [campaignCountriesLanguagesId, limit, fallbackCampaignCountriesLanguagesId, cacheVersion]);

  const { execute: fetchWithRetry } = useRetryableFetch(() => fetchTopSeries(false), { maxRetries: 3 });

  useEffect(() => {
    const isInitialLoad = isInitialMountRef.current;
    const isCampaignJustLoaded = prevCampaignIdRef.current === null && campaignCountriesLanguagesId !== null;
    const isCampaignChanged = prevCampaignIdRef.current !== null && campaignCountriesLanguagesId !== null && prevCampaignIdRef.current !== campaignCountriesLanguagesId;

    if (isInitialLoad) {
      console.log('useTopViewedSeries: 🎯 Initial mount, checking campaign...');
      fetchTopSeries(true);
      isInitialMountRef.current = false;
      prevCampaignIdRef.current = campaignCountriesLanguagesId;
    } else if (isCampaignJustLoaded || isCampaignChanged) {
      console.log(`useTopViewedSeries: 🎯 Campaign ready (${campaignCountriesLanguagesId}), triggering initial fetch...`);
      fetchTopSeries(true);
      prevCampaignIdRef.current = campaignCountriesLanguagesId;
    } else if (cacheVersion > 0) {
      fetchWithRetry();
    }
  }, [campaignCountriesLanguagesId, limit, fallbackCampaignCountriesLanguagesId, cacheVersion, fetchTopSeries, fetchWithRetry]);

  return { series, loading, error };
};

export const useHomepageTop3 = (
  campaignCountriesLanguagesId: string | null,
  contentMetadataCampaignId: string | null = null
) => {
  const [series, setSeries] = useState<Serie[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const cacheVersion = useCacheInvalidationListener();
  const isInitialMountRef = useRef(true);
  const isFetchingRef = useRef(false);
  const prevCampaignIdRef = useRef<string | null>(null);
  const prevContentMetadataIdRef = useRef<string | null>(null);

  const fetchHomepageTop3 = useCallback(async (isInitialLoad: boolean) => {
    if (!campaignCountriesLanguagesId) {
      setSeries([]);
      if (isInitialLoad) setLoading(false);
      return;
    }

    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;

    try {
      if (isInitialLoad) {
        setLoading(true);
      }

      const cachedSeries = await persistentContentCache.getHomepageTop3(campaignCountriesLanguagesId, contentMetadataCampaignId);
      if (cachedSeries && cachedSeries.length > 0) {
        console.log(`useHomepageTop3: Using cached homepage top 3 (${cachedSeries.length} items)`);
        const enrichedCached = await enrichWithSeasonCounts(cachedSeries);
        setSeries(enrichedCached.filter(s => s.thumbnail && s.thumbnail.trim().length > 0));
        setError(null);
        if (isInitialLoad) setLoading(false);
        isFetchingRef.current = false;
        return;
      }

      const data = await ContentService.getHomepageTop3(campaignCountriesLanguagesId, contentMetadataCampaignId);

      if (data && data.length > 0) {
        const enrichedData = await enrichWithSeasonCounts(data);
        await persistentContentCache.setHomepageTop3(campaignCountriesLanguagesId, enrichedData, contentMetadataCampaignId);
        console.log(`useHomepageTop3: Fetched and cached ${data.length} homepage top 3 items`);
        setSeries(enrichedData.filter(s => s.thumbnail && s.thumbnail.trim().length > 0));
        setError(null);
      } else {
        console.log('useHomepageTop3: No Top3 data available');
        setSeries([]);
      }
    } catch (err) {
      const errorMsg = 'Erreur lors du chargement du top 3';
      setError(errorMsg);
      console.error(errorMsg, err);
      setSeries([]);
    } finally {
      if (isInitialLoad) setLoading(false);
      isFetchingRef.current = false;
    }
  }, [campaignCountriesLanguagesId, contentMetadataCampaignId, cacheVersion]);

  const { execute: fetchWithRetry } = useRetryableFetch(() => fetchHomepageTop3(false), { maxRetries: 1 });

  useEffect(() => {
    const isInitialLoad = isInitialMountRef.current;
    const isCampaignJustLoaded = prevCampaignIdRef.current === null && campaignCountriesLanguagesId !== null;
    const isCampaignChanged = prevCampaignIdRef.current !== null && campaignCountriesLanguagesId !== null && prevCampaignIdRef.current !== campaignCountriesLanguagesId;
    // In Morocco the primary campaign id stays fixed (ar) when the user switches
    // language, so also refetch when the content-metadata (title language) changes.
    const isContentLanguageChanged = campaignCountriesLanguagesId !== null && prevContentMetadataIdRef.current !== contentMetadataCampaignId;

    if (isInitialLoad) {
      fetchHomepageTop3(true);
      isInitialMountRef.current = false;
      prevCampaignIdRef.current = campaignCountriesLanguagesId;
      prevContentMetadataIdRef.current = contentMetadataCampaignId;
    } else if (isCampaignJustLoaded || isCampaignChanged || isContentLanguageChanged) {
      fetchHomepageTop3(true);
      prevCampaignIdRef.current = campaignCountriesLanguagesId;
      prevContentMetadataIdRef.current = contentMetadataCampaignId;
    } else if (cacheVersion > 0) {
      fetchWithRetry();
    }
  }, [campaignCountriesLanguagesId, contentMetadataCampaignId, cacheVersion, fetchHomepageTop3, fetchWithRetry]);

  return { series, loading, error };
};

export const useRubriques = (
  campaignCountriesLanguagesId: string | null,
  fallbackCampaignCountriesLanguagesId: string | null = null,
  languagePreferredCampaignId: string | null = null,
  contentMetadataCampaignId: string | null = null,
  waitForCampaign: boolean = true
) => {
  const [rubriques, setRubriques] = useState<Rubrique[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const cacheVersion = useCacheInvalidationListener();
  const isInitialMountRef = useRef(true);
  const isFetchingRef = useRef(false);
  const prevCampaignIdRef = useRef<string | null>(null);

  const fetchRubriques = useCallback(async (isInitialLoad: boolean) => {
    if (!campaignCountriesLanguagesId) {
      if (waitForCampaign && isInitialLoad) {
        console.log('useRubriques: ⏱️ Waiting for campaign configuration...');
      }
      setRubriques([]);
      // Keep loading=true if we're waiting for campaign, otherwise set to false
      if (!waitForCampaign && isInitialLoad) {
        setLoading(false);
      }
      return;
    }

    if (isFetchingRef.current) {
      console.log('⏭️ [useRubriques] Fetch already in progress, skipping...');
      return;
    }

    isFetchingRef.current = true;

    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        console.log(`♻️ [useRubriques] Cache invalidated (v${cacheVersion}), starting silent refetch...`);
      }

      const cachedRubriques = await persistentContentCache.getRubriques(campaignCountriesLanguagesId);
      if (cachedRubriques && cachedRubriques.length > 0) {
        console.log(`useRubriques: ✅ Using cached rubriques (${cachedRubriques.length} items)`);
        setRubriques(cachedRubriques);
        setError(null);
        if (isInitialLoad) setLoading(false);
        isFetchingRef.current = false;
        return;
      }

      // Cache is empty or missing - fetch from Supabase
      if (cachedRubriques && cachedRubriques.length === 0) {
        console.log('useRubriques: ⚠️ Cache returned empty array, fetching from Supabase...');
      } else {
        console.log('useRubriques: 📡 No cache found, fetching from Supabase...');
      }

      const data = await ContentService.getRubriques(campaignCountriesLanguagesId, fallbackCampaignCountriesLanguagesId, languagePreferredCampaignId, contentMetadataCampaignId);

      // Only cache if we have actual data
      if (data && data.length > 0) {
        await persistentContentCache.setRubriques(campaignCountriesLanguagesId, data);
        console.log(`useRubriques: ✅ Fetched and cached ${data.length} rubriques`);
      } else {
        console.log('useRubriques: ⚠️ Supabase returned empty data, not caching');
      }

      setRubriques(data);
      setError(null);

      if (!isInitialLoad) {
        console.log('✅ [useRubriques] Silent refetch completed successfully');
      }
    } catch (err) {
      const errorMsg = 'Erreur lors du chargement des rubriques';
      setError(errorMsg);
      console.error(isInitialLoad ? errorMsg : '❌ [useRubriques] Silent refetch failed:', err);
    } finally {
      if (isInitialLoad) setLoading(false);
      isFetchingRef.current = false;
    }
  }, [campaignCountriesLanguagesId, fallbackCampaignCountriesLanguagesId, languagePreferredCampaignId, contentMetadataCampaignId, waitForCampaign, cacheVersion]);

  const { execute: fetchWithRetry } = useRetryableFetch(() => fetchRubriques(false), { maxRetries: 3 });

  useEffect(() => {
    const isInitialLoad = isInitialMountRef.current;
    const isCampaignJustLoaded = prevCampaignIdRef.current === null && campaignCountriesLanguagesId !== null;
    const isCampaignChanged = prevCampaignIdRef.current !== null && campaignCountriesLanguagesId !== null && prevCampaignIdRef.current !== campaignCountriesLanguagesId;

    if (isInitialLoad) {
      console.log('useRubriques: 🎯 Initial mount, checking campaign...');
      fetchRubriques(true);
      isInitialMountRef.current = false;
      prevCampaignIdRef.current = campaignCountriesLanguagesId;
    } else if (isCampaignJustLoaded || isCampaignChanged) {
      console.log(`useRubriques: 🎯 Campaign ready (${campaignCountriesLanguagesId}), triggering initial fetch...`);
      fetchRubriques(true);
      prevCampaignIdRef.current = campaignCountriesLanguagesId;
    } else if (cacheVersion > 0) {
      fetchWithRetry();
    }
  }, [campaignCountriesLanguagesId, fallbackCampaignCountriesLanguagesId, languagePreferredCampaignId, contentMetadataCampaignId, cacheVersion, fetchRubriques, fetchWithRetry]);

  return { rubriques, loading, error };
};

export const useSeries = (
  campaignCountriesLanguagesId: string | null,
  fallbackCampaignCountriesLanguagesId: string | null = null,
  languagePreferredCampaignId: string | null = null,
  contentMetadataCampaignId: string | null = null,
  waitForCampaign: boolean = true
) => {
  const [series, setSeries] = useState<Serie[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const cacheVersion = useCacheInvalidationListener();
  const isInitialMountRef = useRef(true);
  const isFetchingRef = useRef(false);
  const prevCampaignIdRef = useRef<string | null>(null);

  const fetchSeries = useCallback(async (isInitialLoad: boolean) => {
    if (!campaignCountriesLanguagesId) {
      if (waitForCampaign && isInitialLoad) {
        console.log('useSeries: ⏱️ Waiting for campaign configuration...');
      }
      setSeries([]);
      // Keep loading=true if we're waiting for campaign, otherwise set to false
      if (!waitForCampaign && isInitialLoad) {
        setLoading(false);
      }
      return;
    }

    if (isFetchingRef.current) {
      console.log('⏭️ [useSeries] Fetch already in progress, skipping...');
      return;
    }

    isFetchingRef.current = true;

    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        console.log(`♻️ [useSeries] Cache invalidated (v${cacheVersion}), starting silent refetch...`);
      }

      const cachedSeries = await persistentContentCache.getSeries(campaignCountriesLanguagesId);
      if (cachedSeries && cachedSeries.length > 0) {
        console.log(`useSeries: ✅ Using cached series (${cachedSeries.length} items)`);
        const enriched = await enrichWithSeasonCounts(cachedSeries);
        setSeries(enriched);
        setError(null);
        if (isInitialLoad) setLoading(false);
        isFetchingRef.current = false;
        return;
      }

      // Cache is empty or missing - fetch from Supabase
      if (cachedSeries && cachedSeries.length === 0) {
        console.log('useSeries: ⚠️ Cache returned empty array, fetching from Supabase...');
      } else {
        console.log('useSeries: 📡 No cache found, fetching from Supabase...');
      }

      const data = await ContentService.getSeries(campaignCountriesLanguagesId, fallbackCampaignCountriesLanguagesId, languagePreferredCampaignId, contentMetadataCampaignId);

      // Only cache if we have actual data
      if (data && data.length > 0) {
        await persistentContentCache.setSeries(campaignCountriesLanguagesId, data);
        console.log(`useSeries: ✅ Fetched and cached ${data.length} series`);
      } else {
        console.log('useSeries: ⚠️ Supabase returned empty data, not caching');
      }

      setSeries(data);
      setError(null);

      if (!isInitialLoad) {
        console.log('✅ [useSeries] Silent refetch completed successfully');
      }
    } catch (err) {
      const errorMsg = 'Erreur lors du chargement des séries';
      setError(errorMsg);
      console.error(isInitialLoad ? errorMsg : '❌ [useSeries] Silent refetch failed:', err);
    } finally {
      if (isInitialLoad) setLoading(false);
      isFetchingRef.current = false;
    }
  }, [campaignCountriesLanguagesId, fallbackCampaignCountriesLanguagesId, languagePreferredCampaignId, contentMetadataCampaignId, waitForCampaign, cacheVersion]);

  const { execute: fetchWithRetry } = useRetryableFetch(() => fetchSeries(false), { maxRetries: 3 });

  useEffect(() => {
    const isInitialLoad = isInitialMountRef.current;
    const isCampaignJustLoaded = prevCampaignIdRef.current === null && campaignCountriesLanguagesId !== null;
    const isCampaignChanged = prevCampaignIdRef.current !== null && campaignCountriesLanguagesId !== null && prevCampaignIdRef.current !== campaignCountriesLanguagesId;

    if (isInitialLoad) {
      console.log('useSeries: 🎯 Initial mount, checking campaign...');
      fetchSeries(true);
      isInitialMountRef.current = false;
      prevCampaignIdRef.current = campaignCountriesLanguagesId;
    } else if (isCampaignJustLoaded || isCampaignChanged) {
      console.log(`useSeries: 🎯 Campaign ready (${campaignCountriesLanguagesId}), triggering initial fetch...`);
      fetchSeries(true);
      prevCampaignIdRef.current = campaignCountriesLanguagesId;
    } else if (cacheVersion > 0) {
      fetchWithRetry();
    }
  }, [campaignCountriesLanguagesId, fallbackCampaignCountriesLanguagesId, languagePreferredCampaignId, contentMetadataCampaignId, cacheVersion, fetchSeries, fetchWithRetry]);

  return { series, loading, error };
};

export const useSeriesWithFreeEpisodes = (
  campaignCountriesLanguagesId: string | null,
  fallbackCampaignCountriesLanguagesId: string | null = null
) => {
  const [series, setSeries] = useState<Serie[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const cacheVersion = useCacheInvalidationListener();
  const isInitialMountRef = useRef(true);
  const isFetchingRef = useRef(false);
  const prevCampaignIdRef = useRef<string | null>(null);

  const fetchSeries = useCallback(async (isInitialLoad: boolean) => {
    if (!campaignCountriesLanguagesId) {
      setSeries([]);
      // Don't set loading=false if we might get a campaign later
      return;
    }

    if (isFetchingRef.current) {
      console.log('⏭️ [useSeriesWithFreeEpisodes] Fetch already in progress, skipping...');
      return;
    }

    isFetchingRef.current = true;

    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        console.log(`♻️ [useSeriesWithFreeEpisodes] Cache invalidated (v${cacheVersion}), starting silent refetch...`);
      }

      const cachedSeries = await persistentContentCache.getFreeEpisodesSeries(campaignCountriesLanguagesId);
      if (cachedSeries && cachedSeries.length > 0) {
        console.log(`useSeriesWithFreeEpisodes: ✅ Using cached free episodes series (${cachedSeries.length} items)`);
        const enriched = await enrichWithSeasonCounts(cachedSeries);
        setSeries(enriched);
        setError(null);
        if (isInitialLoad) setLoading(false);
        isFetchingRef.current = false;
        return;
      }

      // Cache is empty or missing - fetch from Supabase
      if (cachedSeries && cachedSeries.length === 0) {
        console.log('useSeriesWithFreeEpisodes: ⚠️ Cache returned empty array, fetching from Supabase...');
      } else {
        console.log('useSeriesWithFreeEpisodes: 📡 No cache found, fetching from Supabase...');
      }

      const data = await ContentService.getSeriesWithFreeEpisodes(campaignCountriesLanguagesId, fallbackCampaignCountriesLanguagesId);

      // Only cache if we have actual data
      if (data && data.length > 0) {
        await persistentContentCache.setFreeEpisodesSeries(campaignCountriesLanguagesId, data);
        console.log(`useSeriesWithFreeEpisodes: ✅ Fetched and cached ${data.length} series with free episodes`);
      } else {
        console.log('useSeriesWithFreeEpisodes: ⚠️ Supabase returned empty data, not caching');
      }

      setSeries(data);
      setError(null);

      if (!isInitialLoad) {
        console.log('✅ [useSeriesWithFreeEpisodes] Silent refetch completed successfully');
      }
    } catch (err) {
      const errorMsg = 'Erreur lors du chargement des séries gratuites';
      setError(errorMsg);
      console.error(isInitialLoad ? errorMsg : '❌ [useSeriesWithFreeEpisodes] Silent refetch failed:', err);
    } finally {
      if (isInitialLoad) setLoading(false);
      isFetchingRef.current = false;
    }
  }, [campaignCountriesLanguagesId, fallbackCampaignCountriesLanguagesId, cacheVersion]);

  const { execute: fetchWithRetry } = useRetryableFetch(() => fetchSeries(false), { maxRetries: 3 });

  useEffect(() => {
    const isInitialLoad = isInitialMountRef.current;
    const isCampaignJustLoaded = prevCampaignIdRef.current === null && campaignCountriesLanguagesId !== null;
    const isCampaignChanged = prevCampaignIdRef.current !== null && campaignCountriesLanguagesId !== null && prevCampaignIdRef.current !== campaignCountriesLanguagesId;

    if (isInitialLoad) {
      console.log('useSeriesWithFreeEpisodes: 🎯 Initial mount, checking campaign...');
      fetchSeries(true);
      isInitialMountRef.current = false;
      prevCampaignIdRef.current = campaignCountriesLanguagesId;
    } else if (isCampaignJustLoaded || isCampaignChanged) {
      console.log(`useSeriesWithFreeEpisodes: 🎯 Campaign ready (${campaignCountriesLanguagesId}), triggering initial fetch...`);
      fetchSeries(true);
      prevCampaignIdRef.current = campaignCountriesLanguagesId;
    } else if (cacheVersion > 0) {
      fetchWithRetry();
    }
  }, [campaignCountriesLanguagesId, fallbackCampaignCountriesLanguagesId, cacheVersion, fetchSeries, fetchWithRetry]);

  return { series, loading, error };
};

export const useSeriesByRubrique = (
  campaignCountriesLanguagesId: string | null,
  rubriqueId: string,
  fallbackCampaignCountriesLanguagesId: string | null = null,
  languagePreferredCampaignId: string | null = null,
  contentMetadataCampaignId: string | null = null,
  waitForCampaign: boolean = true
) => {
  const [series, setSeries] = useState<Serie[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const cacheVersion = useCacheInvalidationListener();
  const isInitialMountRef = useRef(true);
  const isFetchingRef = useRef(false);
  const prevCampaignIdRef = useRef<string | null>(null);

  const fetchSeries = useCallback(async (isInitialLoad: boolean) => {
    if (!campaignCountriesLanguagesId || !rubriqueId) {
      if (waitForCampaign && !campaignCountriesLanguagesId && isInitialLoad) {
        console.log('useSeriesByRubrique: ⏱️ Waiting for campaign configuration...');
      }
      setSeries([]);
      // Keep loading=true if we're waiting for campaign, otherwise set to false
      if (!waitForCampaign && isInitialLoad) {
        setLoading(false);
      }
      return;
    }

    if (isFetchingRef.current) {
      console.log('⏭️ [useSeriesByRubrique] Fetch already in progress, skipping...');
      return;
    }

    isFetchingRef.current = true;

    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        console.log(`♻️ [useSeriesByRubrique] Cache invalidated (v${cacheVersion}) for rubrique ${rubriqueId}, starting silent refetch...`);
      }

      const cachedSeries = await persistentContentCache.getSeriesByRubrique(campaignCountriesLanguagesId, rubriqueId);
      if (cachedSeries && cachedSeries.length > 0) {
        console.log(`useSeriesByRubrique: ✅ Using cached series for rubrique ${rubriqueId} (${cachedSeries.length} items)`);
        const enriched = await enrichWithSeasonCounts(cachedSeries);
        setSeries(enriched);
        setError(null);
        if (isInitialLoad) setLoading(false);
        isFetchingRef.current = false;
        return;
      }

      // Cache is empty or missing - fetch from Supabase
      if (cachedSeries && cachedSeries.length === 0) {
        console.log(`useSeriesByRubrique: ⚠️ Cache returned empty array for rubrique ${rubriqueId}, fetching from Supabase...`);
      } else {
        console.log(`useSeriesByRubrique: 📡 No cache found for rubrique ${rubriqueId}, fetching from Supabase...`);
      }

      const data = await ContentService.getSeriesByRubrique(campaignCountriesLanguagesId, rubriqueId, fallbackCampaignCountriesLanguagesId, languagePreferredCampaignId, contentMetadataCampaignId);

      // Only cache if we have actual data
      if (data && data.length > 0) {
        await persistentContentCache.setSeriesByRubrique(campaignCountriesLanguagesId, rubriqueId, data);
        console.log(`useSeriesByRubrique: ✅ Fetched and cached ${data.length} series for rubrique ${rubriqueId}`);
      } else {
        console.log(`useSeriesByRubrique: ⚠️ Supabase returned empty data for rubrique ${rubriqueId}, not caching`);
      }

      setSeries(data);
      setError(null);

      if (!isInitialLoad) {
        console.log(`✅ [useSeriesByRubrique] Silent refetch completed for rubrique ${rubriqueId}`);
      }
    } catch (err) {
      const errorMsg = 'Erreur lors du chargement des séries';
      setError(errorMsg);
      console.error(isInitialLoad ? errorMsg : `❌ [useSeriesByRubrique] Silent refetch failed for rubrique ${rubriqueId}:`, err);
    } finally {
      if (isInitialLoad) setLoading(false);
      isFetchingRef.current = false;
    }
  }, [campaignCountriesLanguagesId, rubriqueId, fallbackCampaignCountriesLanguagesId, languagePreferredCampaignId, contentMetadataCampaignId, waitForCampaign, cacheVersion]);

  const { execute: fetchWithRetry } = useRetryableFetch(() => fetchSeries(false), { maxRetries: 3 });

  useEffect(() => {
    const isInitialLoad = isInitialMountRef.current;
    const isCampaignJustLoaded = prevCampaignIdRef.current === null && campaignCountriesLanguagesId !== null;
    const isCampaignChanged = prevCampaignIdRef.current !== null && campaignCountriesLanguagesId !== null && prevCampaignIdRef.current !== campaignCountriesLanguagesId;

    if (isInitialLoad) {
      //console.log(`useSeriesByRubrique: 🎯 Initial mount for rubrique ${rubriqueId}, checking campaign...`);
      fetchSeries(true);
      isInitialMountRef.current = false;
      prevCampaignIdRef.current = campaignCountriesLanguagesId;
    } else if (isCampaignJustLoaded || isCampaignChanged) {
      
      //console.log(`useSeriesByRubrique: 🎯 Campaign ready (${campaignCountriesLanguagesId}), triggering initial fetch for rubrique ${rubriqueId}...`);
      fetchSeries(true);
      prevCampaignIdRef.current = campaignCountriesLanguagesId;
    } else if (cacheVersion > 0) {
      fetchWithRetry();
    }
  }, [campaignCountriesLanguagesId, rubriqueId, fallbackCampaignCountriesLanguagesId, languagePreferredCampaignId, contentMetadataCampaignId, cacheVersion, fetchSeries, fetchWithRetry]);

  return { series, loading, error };
};

export const useResumeWatchingSeries = (
  campaignCountriesLanguagesId: string | null,
  limit: number = 10
) => {
  const [series, setSeries] = useState<Serie[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { authState } = useAuth();

  useEffect(() => {
    if (authState.isLoading) {
      return;
    }

    if (!authState.user?.smartuserId) {
      setSeries([]);
      setLoading(false);
      return;
    }

    if (!campaignCountriesLanguagesId) {
      setLoading(false);
      return;
    }

    const fetchResumeWatchingSeries = async () => {

      try {
        setLoading(true);
        setError(null);

        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          console.log('[useResumeWatchingSeries] Query timeout after 5000ms, aborting...');
          controller.abort();
        }, 5000);

        try {
          const { data: progressData, error: progressError } = await withSupabaseResult<any[]>(
            supabase
              .from('user_viewing_progress')
              .select('content_id, updated_at, serie_id')
              .eq('smartuser_id', authState.user.smartuserId)
              .eq('content_type', 'episode')
              .eq('is_completed', false)
              .order('updated_at', { ascending: false })
              .limit(limit * 5)
              .abortSignal(controller.signal),
            { operation: 'useContent.resumeWatching.progress', logger: contentLog, fallback: [] }
          );

          clearTimeout(timeoutId);

          if (progressError) {
            console.error('Error fetching viewing progress:', progressError.message);
            setError('Error loading resume watching');
            return;
          }

          if (!progressData || progressData.length === 0) {
            setSeries([]);
            setLoading(false);
            return;
          }

          const progressWithSerieId = progressData.filter(p => p.serie_id != null);
          const progressWithoutSerieId = progressData.filter(p => p.serie_id == null);

          let episodeToSeriesMap = new Map<number, number>();

          if (progressWithoutSerieId.length > 0) {
            console.log(`[useResumeWatchingSeries] ${progressWithoutSerieId.length} legacy records without serie_id, fetching from unified episodes table...`);
            const episodeIds = progressWithoutSerieId.map(p => p.content_id);
            const { data: episodesData, error: episodesError } = await withSupabaseResult<{ episode_id: number; series_id: number }[]>(
              supabase
                .from('unified_contents_series_episodes')
                .select('episode_id, series_id')
                .in('episode_id', episodeIds),
              { operation: 'useContent.resumeWatching.legacyEpisodes', logger: contentLog, fallback: [] }
            );

            if (episodesError) {
              console.error('Error fetching episode details for legacy records:', episodesError.message);
            } else if (episodesData) {
              episodeToSeriesMap = new Map(episodesData.map(e => [e.episode_id, e.series_id]));
            }
          }

          const seenSeriesIds = new Set<number>();
          const orderedSeriesIds: number[] = [];

          for (const progress of progressData) {
            const seriesId = progress.serie_id ?? episodeToSeriesMap.get(progress.content_id);
            if (seriesId && !seenSeriesIds.has(seriesId)) {
              seenSeriesIds.add(seriesId);
              orderedSeriesIds.push(seriesId);
              if (orderedSeriesIds.length >= limit) break;
            }
          }

          console.log(`[useResumeWatchingSeries] Optimized: ${progressWithSerieId.length} records used denormalized serie_id, ${progressWithoutSerieId.length} used fallback`);

          if (orderedSeriesIds.length === 0) {
            setSeries([]);
            setLoading(false);
            return;
          }

          const [seriesResult, seasonsResult] = await Promise.all([
            withSupabaseResult<any[]>(
              supabase
                .from('contents_series')
                .select('serie_id, title, description, url_covers')
                .eq('campaign_countries_languages_id', campaignCountriesLanguagesId)
                .in('serie_id', orderedSeriesIds),
              { operation: 'useContent.resumeWatching.seriesDetails', logger: contentLog, fallback: [] }
            ),
            withSupabaseResult<any[]>(
              supabase
                .from('unified_contents_series_episodes')
                .select('series_id, season_position')
                .in('series_id', orderedSeriesIds.map(id => parseInt(String(id)))),
              { operation: 'useContent.resumeWatching.seasons', logger: contentLog, fallback: [] }
            ),
          ]);

          if (seriesResult.error) {
            console.error('Error fetching series details:', seriesResult.error);
            setError('Error loading resume watching');
            return;
          }

          const seasonCountMap = new Map<number, number>();
          if (seasonsResult.data) {
            const seasonsBySeriesId = new Map<number, Set<number>>();
            for (const ep of seasonsResult.data) {
              if (!seasonsBySeriesId.has(ep.series_id)) {
                seasonsBySeriesId.set(ep.series_id, new Set());
              }
              seasonsBySeriesId.get(ep.series_id)!.add(ep.season_position);
            }
            for (const [seriesId, seasons] of seasonsBySeriesId) {
              seasonCountMap.set(seriesId, seasons.size);
            }
          }

          const seriesMap = new Map(seriesResult.data?.map(s => [s.serie_id, s]) || []);

          const orderedSeries: Serie[] = orderedSeriesIds
            .map(seriesId => {
              const s = seriesMap.get(seriesId);
              if (!s) return null;
              return {
                id: String(s.serie_id),
                title: s.title || 'Unknown Series',
                description: s.description || '',
                thumbnail: s.url_covers || '',
                totalSeasons: seasonCountMap.get(Number(s.serie_id)) || 1,
                isNew: false,
                isTrending: false,
              } as Serie;
            })
            .filter((s): s is Serie => s !== null);

          setSeries(orderedSeries);
          console.log(`useResumeWatchingSeries: Found ${orderedSeries.length} series to resume`);
        } catch (innerErr: any) {
          clearTimeout(timeoutId);

          if (innerErr?.name === 'AbortError' || innerErr?.message?.includes('aborted') || innerErr?.message?.includes('Aborted')) {
            console.log('[useResumeWatchingSeries] Query aborted due to timeout. Showing empty list.');
            setSeries([]);
            setError(null);
            return;
          }

          console.error('Error in resume watching query:', innerErr);
          setError('Error loading resume watching');
        }
      } catch (err: any) {
        console.log('[useResumeWatchingSeries] Unexpected error:', err?.message || 'Unknown error');
        setSeries([]);
        setError(null);
      } finally {
        setLoading(false);
      }
    };

    fetchResumeWatchingSeries();
  }, [authState.isLoading, authState.user?.smartuserId, campaignCountriesLanguagesId, limit]);

  return { series, loading, error };
};